import React from "react";
import Seo from "../components/Seo";

const SHIPPING = [
  {
    title: "Free worldwide shipping over $200",
    body: (
      <>
        Every order over <strong>$200 USD</strong> ships free, fully insured, with tracking.
        Orders under $200 are charged a flat $15 tracked-shipping fee calculated at checkout.
      </>
    ),
  },
  {
    title: "Processing time",
    body: (
      <>
        Orders are authenticated, inspected, and dispatched within <strong>1–2 business days</strong> of
        payment confirmation. You will receive a tracking number by email the moment your bag
        leaves our studio.
      </>
    ),
  },
  {
    title: "Delivery times",
    body: (
      <>
        <ul className="mt-2 space-y-2 list-disc pl-5">
          <li><strong>United States</strong> — 2–4 business days</li>
          <li><strong>Canada</strong> — 3–6 business days</li>
          <li><strong>Europe &amp; UK</strong> — 4–7 business days</li>
          <li><strong>Rest of world</strong> — 5–10 business days</li>
        </ul>
        Delivery times are estimates. Customs clearance on international orders occasionally
        adds 2–3 days.
      </>
    ),
  },
  {
    title: "Duties, taxes & customs",
    body: (
      <>
        International buyers are responsible for any import duties, VAT, or customs fees levied
        by the destination country. These charges are not included in the product price or
        shipping fee and are collected directly by the courier upon delivery.
      </>
    ),
  },
  {
    title: "Signature on delivery",
    body: (
      <>
        For your protection, every shipment requires a <strong>signature on delivery</strong>. If
        you'd prefer to authorise an unattended delivery, please reply to your shipping
        confirmation email and we'll coordinate directly with the courier.
      </>
    ),
  },
];

const RETURNS = [
  {
    title: "14-day return window",
    body: (
      <>
        You may return any bag within <strong>14 days</strong> of receipt for a full refund of the
        product price. The item must be in its original, unused condition with all original
        tags, dust bag, and packaging intact.
      </>
    ),
  },
  {
    title: "How to start a return",
    body: (
      <>
        Email{" "}
        <a href="mailto:concierge@elegantaffairs.shop?subject=Return%20Request" className="underline">
          concierge@elegantaffairs.shop
        </a>{" "}
        with your order number (format <em>EA-XXXXXXXX</em>) and a short reason for the return.
        We'll reply within one business day with a prepaid return label or a shipping address
        depending on your location.
      </>
    ),
  },
  {
    title: "Refunds",
    body: (
      <>
        Once your return is received and inspected, a refund is issued to the original payment
        method within <strong>5 business days</strong>. Original shipping fees (if any) are
        non-refundable. Return shipping is the buyer's responsibility unless the item arrived
        damaged, incorrect, or is covered by our authenticity guarantee.
      </>
    ),
  },
  {
    title: "Exchanges",
    body: (
      <>
        Because our inventory is one-of-one, we don't offer direct exchanges. Please initiate a
        return and place a new order for the piece you'd like to swap into.
      </>
    ),
  },
  {
    title: "Final sale items",
    body: (
      <>
        Items clearly marked <em>Final Sale</em> on the product page are not eligible for
        return unless defective or inauthentic.
      </>
    ),
  },
];

export default function ShippingReturns() {
  return (
    <div className="pt-32 pb-24" data-testid="shipping-returns-page">
      <Seo
        title="Shipping &amp; Returns"
        description="Free worldwide shipping over $200, 1–2 day processing, 14-day returns. Full policy details for Elegant Affairs customers."
      />
      <div className="max-w-[860px] mx-auto px-6 md:px-12">
        <p className="eyebrow">Policy</p>
        <h1 className="font-display text-5xl md:text-7xl mt-4 leading-[0.95] tracking-tighter">
          Shipping &amp; Returns.
        </h1>
        <p className="text-[var(--mn-ink-2)] mt-6 leading-relaxed">
          Fast, insured delivery worldwide. A 14-day return window on every order. Straightforward,
          the way luxury should be.
        </p>
        <p className="text-xs uppercase tracking-[0.22em] text-[var(--mn-muted)] mt-4">
          Last updated: April 2026
        </p>

        <div className="mt-16">
          <h2 className="font-display text-3xl md:text-4xl tracking-tight">Shipping</h2>
          <div className="mt-10 space-y-12">
            {SHIPPING.map((s) => (
              <section key={s.title}>
                <h3 className="font-display text-xl md:text-2xl tracking-tight">{s.title}</h3>
                <div className="text-[var(--mn-ink-2)] leading-relaxed mt-3 text-base">{s.body}</div>
              </section>
            ))}
          </div>
        </div>

        <div className="mt-24">
          <h2 className="font-display text-3xl md:text-4xl tracking-tight">Returns</h2>
          <div className="mt-10 space-y-12">
            {RETURNS.map((s) => (
              <section key={s.title}>
                <h3 className="font-display text-xl md:text-2xl tracking-tight">{s.title}</h3>
                <div className="text-[var(--mn-ink-2)] leading-relaxed mt-3 text-base">{s.body}</div>
              </section>
            ))}
          </div>
        </div>

        <div className="mt-20 p-6 border-l-2 border-[var(--mn-accent)] bg-neutral-50/60">
          <p className="text-xs uppercase tracking-[0.22em] text-[var(--mn-muted)] mb-2">
            Questions?
          </p>
          <p className="text-base text-[var(--mn-ink)]">
            Email{" "}
            <a href="mailto:concierge@elegantaffairs.shop" className="underline">
              concierge@elegantaffairs.shop
            </a>{" "}
            — we reply within 24 hours.
          </p>
        </div>
      </div>
    </div>
  );
}
